import Animation from '../../../../Animation/Animation';
import {
    LiteTradePortfolioPositionPlAnimationContainerStyled,
    LiteTradePortfolioPositionPlAnimationContentStyled,
} from '../styled/LiteTradePortfolioStyled';
import { PropsWithChildren, useCallback, useEffect, useRef } from 'react';
import { LottieRefCurrentProps } from 'lottie-react';
import usePlAnimationWrapperVisibleObserver from './usePlAnimationWrapperVisibleObserver';
import classNames from 'classnames';
import useAnimationByStages from '../../../../../hooks/useAnimationByStages';
import { ILiteTradePortfolioPosition } from '../../../types/liteTradeReducer.t';
import { useDispatch, useSelector } from 'react-redux';
import { getPositionPlLastSeenSignDataSelector } from '../../../liteTradeSelectors';
import { getUserSelectedAccountCode } from '../../../../../app/appSelectors';
import { updatePositionPlLastSeenSignByPositionCodeAction } from '../../../liteTradeActions';

interface ILiteTradePortfolioRowPlAnimationConfig {
    animationDurationMs: number;
    animationType: string;
    plSizeMultiplier: number;
    plColor: string | null;
    textShadow: string | null;
}

const ANIMATION_STAGES_CONFIGS: ILiteTradePortfolioRowPlAnimationConfig[] = [
    {
        animationDurationMs: 250,
        animationType: 'ease-in',
        plSizeMultiplier: 1,
        plColor: null,
        textShadow: null,
    },
    {
        animationDurationMs: 250,
        animationType: 'ease-out',
        plSizeMultiplier: 1.15,
        plColor: 'var(--color-button-primary-default)',
        textShadow: '0 0 10px var(--color-button-primary-default)',
    },
    {
        animationDurationMs: 250,
        animationType: 'ease-in',
        plSizeMultiplier: 1,
        plColor: 'var(--color-button-primary-default)',
        textShadow: '0 0 10px var(--color-button-primary-default)',
    },
    {
        animationDurationMs: 250,
        animationType: 'ease-in',
        plSizeMultiplier: 1,
        plColor: null,
        textShadow: null,
    },
    {
        animationDurationMs: 500,
        animationType: 'ease-in',
        plSizeMultiplier: 1,
        plColor: null,
        textShadow: null,
    },
];

type ILiteTradePortfolioRowAnimationWrapperProps = PropsWithChildren<{
    item: ILiteTradePortfolioPosition,
    observerRoot?: IntersectionObserverInit['root'],
    observerRootMargin?: IntersectionObserverInit['rootMargin'],
    plAmount: number | null,
}>

function LiteTradePortfolioRowPlAnimationWrapper(props: ILiteTradePortfolioRowAnimationWrapperProps) {
    const { children, item, observerRootMargin, plAmount } = props;
    const starsRef = useRef<LottieRefCurrentProps | null>(null);
    const starsShiningRef = useRef<LottieRefCurrentProps | null>(null);
    const selectedAccountCode = useSelector(getUserSelectedAccountCode);
    const positionPlSeenSignData = useSelector(getPositionPlLastSeenSignDataSelector);
    const plLastSeenSign = positionPlSeenSignData?.[selectedAccountCode]?.[item.positionCode];
    const dispatch = useDispatch();

    const updatePositionPlLastSeenSignHandler = useCallback((sign) => {
        dispatch(updatePositionPlLastSeenSignByPositionCodeAction({
            accountCode: selectedAccountCode,
            positionCode: item.positionCode,
            sign,
        }));
    }, [dispatch, item, selectedAccountCode]);

    const { isElementVisible, elementRef } = usePlAnimationWrapperVisibleObserver({
        rootMargin: observerRootMargin,
    });
    const isPlAmountPositive = plAmount > 0;
    const isPlAmountInited = plAmount !== null;

    const {
        isAnimationRunning: isCSSAnimationRunning,
        currentAnimationStageConfig,
        debouncedStartAnimation,
    } = useAnimationByStages<ILiteTradePortfolioRowPlAnimationConfig>({
        animationStagesConfigs: ANIMATION_STAGES_CONFIGS,
    });

    // Start animation and update last seen sign
    useEffect(() => {
        if (!isPlAmountInited) {
            return;
        }
        if (!isCSSAnimationRunning && isElementVisible && isPlAmountPositive && plLastSeenSign !== 'plus') {
            debouncedStartAnimation(0);
        }
        if (isElementVisible && (!plLastSeenSign || (isPlAmountPositive && plLastSeenSign === 'minus') || (!isPlAmountPositive && plLastSeenSign === 'plus'))) {
            updatePositionPlLastSeenSignHandler(isPlAmountPositive ? 'plus' : 'minus');
        }
    }, [debouncedStartAnimation, isCSSAnimationRunning, isElementVisible, isPlAmountInited, isPlAmountPositive, item, plAmount, plLastSeenSign, updatePositionPlLastSeenSignHandler]);

    // Run/stop lottie animations
    useEffect(() => {
        [
            {
                isRun: isCSSAnimationRunning,
                lottieAnimationName: 'Stars',
                ref: starsRef,
            },
            {
                isRun: !isCSSAnimationRunning && isElementVisible && isPlAmountPositive,
                lottieAnimationName: 'StarsShining',
                ref: starsShiningRef,
            },
        ].forEach(({ isRun, ref }) => {
            if (isRun) {
                ref.current?.play();
            } else {
                ref.current?.stop();
            }
        });
    }, [currentAnimationStageConfig, isCSSAnimationRunning, isElementVisible, isPlAmountPositive]);

    const plColor = currentAnimationStageConfig.plColor
        ? currentAnimationStageConfig.plColor
        : isPlAmountPositive
            ? 'var(--color-label-positive)'
            : 'var(--color-label-negative)';

    return <>
        <LiteTradePortfolioPositionPlAnimationContentStyled
            animationDurationMs={currentAnimationStageConfig.animationDurationMs}
            animationType={currentAnimationStageConfig.animationType}
            className={classNames({
                'animation-running': isCSSAnimationRunning,
            })}
            color={plColor}
            plSizeMultiplier={currentAnimationStageConfig.plSizeMultiplier}
            ref={elementRef}
            textShadow={currentAnimationStageConfig.textShadow}
        >
            {children}
        </LiteTradePortfolioPositionPlAnimationContentStyled>
        <LiteTradePortfolioPositionPlAnimationContainerStyled>
            <div className={'position-relative'}>
                <Animation
                    autoplay={false}
                    className={'animation'}
                    loop={false}
                    lottieRef={starsRef}
                    name={'Stars'}
                    renderer={'canvas'}
                />
                <Animation
                    autoplay={false}
                    className={'animation'}
                    loop={true}
                    lottieRef={starsShiningRef}
                    name={'StarsShining'}
                    renderer={'canvas'}
                />
            </div>
        </LiteTradePortfolioPositionPlAnimationContainerStyled>
    </>;
}

export default LiteTradePortfolioRowPlAnimationWrapper;
